import { Table, Column } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatDate } from '@/lib/utils';

export interface RecordingConsentSummary {
  id: string;
  userId: string;
  userEmail: string;
  userName?: string | null;
  printedName: string;
  consentType: 'video' | 'audio' | 'either';
  signedAt: string;
  revokedAt?: string | null;
  isCurrent: boolean;
}

export interface RecordingConsentTableProps {
  consents: RecordingConsentSummary[];
  isLoading?: boolean;
  onView: (consent: RecordingConsentSummary) => void;
}

function consentTypeLabel(type: RecordingConsentSummary['consentType']) {
  switch (type) {
    case 'video':
      return 'Video';
    case 'audio':
      return 'Audio';
    case 'either':
      return 'Video or audio';
    default:
      return type;
  }
}

export function RecordingConsentTable({
  consents,
  isLoading = false,
  onView,
}: RecordingConsentTableProps) {
  const columns: Column<RecordingConsentSummary>[] = [
    {
      key: 'user',
      header: 'User',
      sortable: true,
      render: (consent) => (
        <div className="flex flex-col">
          <span className="font-medium text-slate-900 dark:text-white">
            {consent.userName || consent.printedName}
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {consent.userEmail}
          </span>
        </div>
      ),
    },
    {
      key: 'printedName',
      header: 'Printed name',
      sortable: true,
      render: (consent) => (
        <span className="text-slate-700 dark:text-slate-300">{consent.printedName}</span>
      ),
    },
    {
      key: 'consentType',
      header: 'Participation',
      sortable: true,
      render: (consent) => (
        <span className="text-slate-600 dark:text-slate-400 text-sm">
          {consentTypeLabel(consent.consentType)}
        </span>
      ),
    },
    {
      key: 'signedAt',
      header: 'Signed',
      sortable: true,
      render: (consent) => (
        <span className="text-slate-600 dark:text-slate-400">
          {formatDate(consent.signedAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (consent) => (
        <Badge
          variant={consent.isCurrent ? 'success' : consent.revokedAt ? 'warning' : 'default'}
        >
          {consent.isCurrent ? 'Current' : consent.revokedAt ? 'Revoked' : 'Superseded'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '120px',
      render: (consent) => (
        <Button variant="ghost" size="sm" onClick={() => onView(consent)}>
          View form
        </Button>
      ),
    },
  ];

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      <Table
        columns={columns}
        data={consents}
        keyExtractor={(consent) => consent.id}
        isLoading={isLoading}
        emptyMessage="No signed consent forms found"
      />
    </div>
  );
}
